<script setup lang="ts">
import { computed } from "vue"
import { type Order } from "@/shop/pages/checkout/CheckoutConfirmationPage.vue"
import { useShopAppConfig } from "@/shop/composables"

const { appConfig } = useShopAppConfig()

const props = withDefaults(
    defineProps<{
        order?: Order
    }>(),
    {
        order: undefined,
    },
)

function itemsOfType(type: string) {
    return computed(() => props.order?.items?.filter(item => !item.isSpecialType && item.article?.type === type))
}

const essences = itemsOfType("essence")
const xtracts = itemsOfType("xtracts")
const cannabis = itemsOfType("cannabis")
const readymade = itemsOfType("readymade")
const milled = itemsOfType("milled")
const smalls = itemsOfType("smalls")

const prescriptionItemCount = computed(() =>
    [essences, xtracts, cannabis, readymade, milled, smalls].reduce(
        (count, items) => count + (items.value?.length ?? 0),
        0,
    ),
)
</script>

<template>
    <h2 class="text-dark">Wichtige Hinweise zum Rezept</h2>
    <template
        v-if="
            prescriptionItemCount > 1 &&
            (appConfig.user.payType === 'public_insurance' || appConfig.user.payType === 'public_free')
        "
    >
        <div>
            Bitte beachte, dass für jedes Produkt ein <b>separates Rezept</b> erforderlich ist. (Das heißt: Auf jedem
            Rezept darf nur ein Produkt stehen.)
        </div>
        <div class="mt-2">
            Bitte achte außerdem darauf, dass auf deinem Rezept deine gewünschte Sorte <b>exakt</b> bezeichnet wird.
            Folgende Angaben zu Sorte und Mengen <b>müssen</b> auf deinem Rezept stehen:
        </div>
    </template>
    <div v-else>
        Bitte achte darauf, dass auf deinem Rezept deine gewünschte Sorte <b>exakt</b> bezeichnet wird. Folgende Angaben
        zu Sorte und Mengen <b>müssen</b> auf deinem Rezept stehen:
    </div>

    <div>
        <div v-if="milled?.length" class="my-2">
            Bei der Bestellung von <i>CANN24 Milled</i> Artikeln muss auf dem Rezept der Zusatzvermerk
            <b>zerkleinert</b> vorhanden sein.
        </div>
        <div v-if="cannabis?.length" class="my-2">
            <b>Cannabisblüten</b>
            <ul>
                <li v-for="item in cannabis" :key="item.id">
                    <b>{{ item.article?.publicNamePrimary }}</b> <i>({{ item.article?.publicNameSecondary }})*</i>
                    {{ item.displayTotalAmount }}{{ item.displayUnit }}
                </li>
            </ul>
        </div>
        <div v-if="essences?.length" class="my-2">
            <b>Cannabisextrakte</b>
            <ul>
                <li v-for="item in essences" :key="item.id">
                    <b>{{ item.article?.publicNamePrimary }}</b> {{ item.displayTotalAmount }}{{ item.displayUnit }}
                </li>
            </ul>
        </div>
        <div v-if="xtracts?.length" class="my-2">
            <b>CANN24 Xtract</b>
            <ul>
                <li v-for="item in xtracts" :key="item.id">
                    <b>{{ item.article?.publicNamePrimary }}</b>
                    {{ Number(item.displayTotalAmount) }}{{ item.batchPreparationPackageDisplayUnit }}
                </li>
            </ul>
        </div>
        <div v-if="readymade?.length" class="my-2">
            <b>CANN24 Readymade</b>
            <ul>
                <li v-for="item in readymade" :key="item.id">
                    <b>{{ item.article?.publicNamePrimary }}</b>
                    {{ Number(item.displayTotalAmount) }}{{ item.batchPreparationPackageDisplayUnit }}
                </li>
            </ul>
        </div>
        <div v-if="milled?.length" class="my-2">
            <b>CANN24 Milled</b>
            <ul>
                <li v-for="item in milled" :key="item.id">
                    <b>{{ item.article?.publicNamePrimary }}</b> <i>({{ item.article?.publicNameSecondary }})*</i>
                    {{ Number(item.displayTotalAmount) }}{{ item.batchPreparationPackageDisplayUnit }} - zerkleinert
                </li>
            </ul>
        </div>
        <div v-if="smalls?.length" class="my-2">
            <b>CANN24 Smalls</b>
            <ul>
                <li v-for="item in smalls" :key="item.id">
                    <b>{{ item.article?.publicNamePrimary }}</b>
                    {{ Number(item.displayTotalAmount) }}{{ item.batchPreparationPackageDisplayUnit }}
                </li>
            </ul>
        </div>
    </div>
    <span v-if="cannabis?.length || milled?.length" class="text-muted small"><i>*zusätzlich optional</i></span>
</template>
